// ReceivedDocumentsPanel (Feb 2026)
// -----------------------------------------------------------------
// Renders every uploaded receipt document for a single order with
// preview/download affordances. Fetches each doc as a Blob via the
// authenticated backend endpoint (branch or admin — the caller
// supplies the base URL) and cracks open an inline preview inside
// a lightweight lightbox for images / PDFs. Text files & other
// unknown MIME types drop straight to a Download link.
//
// Usage:
//   <ReceivedDocumentsPanel
//     docs={order.received_documents}
//     baseUrl={`${API}/admin/orders/${order.id}/received-documents`}
//     authHeaders={authHeaders}
//     testidPrefix={`admin-order-${order.id}`}
//   />

import React, { useEffect, useMemo, useState } from 'react';
import { FileText, ImageIcon, Download, X, Eye, ChevronDown, ChevronRight, Trash2, CheckSquare, Square, Loader2 } from 'lucide-react';
import axios from 'axios';

const _fmtSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const _kind = (mime = '') => {
  if (mime.startsWith('image/')) return 'image';
  if (mime === 'application/pdf') return 'pdf';
  return 'other';
};

const _iconFor = (mime) => (_kind(mime) === 'image' ? ImageIcon : FileText);

const ReceivedDocumentsPanel = ({
  docs = [],
  baseUrl,
  authHeaders = {},
  testidPrefix = 'received-docs',
  label = 'Received documents',
  defaultExpanded = false,
  // Feb 2026 — Bulk delete gate. When true (super admin or holder of
  // `allow_delete_received_documents`) the panel shows a Select /
  // Select all / Delete N selected toolbar. When false the panel
  // stays fully read-only. `orderId` + `bulkDeleteUrl` are required
  // to actually issue the DELETE — the caller picks admin vs branch
  // path so this component doesn't hard-code auth prefixes.
  canDelete = false,
  bulkDeleteUrl = '',
  onDocsDeleted,
}) => {
  const [preview, setPreview] = useState(null); // {name, mime, url}
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [localDocs, setLocalDocs] = useState(docs);

  useEffect(() => { setLocalDocs(docs); }, [docs]);

  const trashEnabled = !!(canDelete && bulkDeleteUrl);

  const toggleSelect = (fid) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(fid)) next.delete(fid); else next.add(fid);
    return next;
  });
  const allIds = useMemo(() => localDocs.map((d) => d.file_id), [localDocs]);
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.has(id));
  const selectAll = () => setSelectedIds(allSelected ? new Set() : new Set(allIds));
  const exitSelectMode = () => { setSelectMode(false); setSelectedIds(new Set()); };

  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (typeof window !== 'undefined'
      && !window.confirm(
        `Permanently delete ${ids.length} received document${ids.length === 1 ? '' : 's'}?\n\nThey will be removed from the database AND from Google Drive. This cannot be undone.`
      )) {
      return;
    }
    setBulkDeleting(true);
    try {
      const { data } = await axios.post(
        bulkDeleteUrl,
        { file_ids: ids },
        { headers: authHeaders },
      );
      setLocalDocs((prev) => prev.filter((d) => !selectedIds.has(d.file_id)));
      setSelectedIds(new Set());
      setSelectMode(false);
      if (onDocsDeleted) ids.forEach((id) => onDocsDeleted(id));
      if (typeof window !== 'undefined' && (data?.drive_errors || []).length) {
        window.alert(
          `${data.deleted} document(s) deleted from the database, but ${data.drive_errors.length} Drive file(s) could not be removed. Ops will retry later.`
        );
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Bulk delete failed';
      if (typeof window !== 'undefined') window.alert(`Could not delete: ${msg}`);
    } finally {
      setBulkDeleting(false);
    }
  };

  const openPreview = async (doc) => {
    try {
      const res = await axios.get(`${baseUrl}/${doc.file_id}`, {
        headers: authHeaders,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      setPreview({ name: doc.name, mime: doc.mime, url });
    } catch (e) {
       
      window.alert(e?.response?.data?.detail || 'Could not open document');
    }
  };

  const download = async (doc) => {
    try {
      const res = await axios.get(`${baseUrl}/${doc.file_id}`, {
        headers: authHeaders,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.name || 'document';
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Give the browser a beat to trigger the download before we
      // revoke the blob URL — some browsers race here.
      setTimeout(() => URL.revokeObjectURL(url), 2500);
    } catch (e) {
       
      window.alert(e?.response?.data?.detail || 'Download failed');
    }
  };

  const closePreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  useEffect(() => {
    // Cleanup on unmount so we don't leak blob URLs.
    return () => {
      if (preview?.url) URL.revokeObjectURL(preview.url);
    };
  }, []);

  if (!localDocs || localDocs.length === 0) return null;

  return (
    <div className="mt-3 rounded-lg border border-neutral-200 bg-white overflow-hidden" data-testid={`${testidPrefix}-received-docs`}>
      {/* Feb 2026 — collapsible header, identical style + behaviour to
          LoadingPicturesSection so both cards read as siblings. Click
          anywhere on the row to toggle. */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-neutral-50 transition text-left"
        aria-expanded={expanded}
        data-testid={`${testidPrefix}-received-docs-toggle`}
      >
        {expanded ? (
          <ChevronDown className="w-4 h-4 text-neutral-500 shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-neutral-500 shrink-0" />
        )}
        <FileText className="w-4 h-4 text-emerald-600 shrink-0" />
        <div className="text-[11px] font-bold uppercase tracking-wider text-[#0F172A]">
          {label}
        </div>
        <span
          className="text-[10px] font-semibold rounded-full px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200"
          data-testid={`${testidPrefix}-received-docs-count`}
        >
          {localDocs.length}
        </span>
      </button>
      {expanded && (
        <div className="border-t border-neutral-200 p-3" data-testid={`${testidPrefix}-received-docs-body`}>
          {trashEnabled && (
            <div className="flex flex-wrap items-center gap-2 pb-2 mb-2 border-b border-neutral-100"
                 data-testid={`${testidPrefix}-received-docs-bulk-toolbar`}>
              {!selectMode ? (
                <button type="button"
                        onClick={() => setSelectMode(true)}
                        className="inline-flex items-center gap-1 rounded-full border border-neutral-300 bg-white hover:bg-neutral-50 text-[11px] font-semibold px-2.5 py-1 text-neutral-700"
                        data-testid={`${testidPrefix}-received-docs-select-mode-btn`}>
                  <CheckSquare className="w-3.5 h-3.5" /> Select
                </button>
              ) : (
                <>
                  <button type="button"
                          onClick={exitSelectMode}
                          className="inline-flex items-center gap-1 rounded-full border border-neutral-300 bg-white hover:bg-neutral-50 text-[11px] font-semibold px-2.5 py-1 text-neutral-600"
                          data-testid={`${testidPrefix}-received-docs-select-cancel-btn`}>
                    <X className="w-3.5 h-3.5" /> Cancel
                  </button>
                  <button type="button"
                          onClick={selectAll}
                          className="inline-flex items-center gap-1 rounded-full border border-sky-300 bg-white hover:bg-sky-50 text-[11px] font-semibold px-2.5 py-1 text-sky-700"
                          data-testid={`${testidPrefix}-received-docs-select-all-btn`}>
                    {allSelected ? 'Deselect all' : 'Select all'}
                  </button>
                  <span className="text-[11px] font-semibold text-neutral-500" data-testid={`${testidPrefix}-received-docs-select-count`}>
                    {selectedIds.size} selected
                  </span>
                  {selectedIds.size > 0 && (
                    <button type="button"
                            onClick={bulkDelete}
                            disabled={bulkDeleting}
                            className="ml-auto inline-flex items-center gap-1 rounded-full border border-red-600 bg-red-600 hover:bg-red-700 text-white text-[11px] font-bold px-3 py-1 disabled:opacity-60"
                            data-testid={`${testidPrefix}-received-docs-bulk-delete-btn`}>
                      {bulkDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                      Delete {selectedIds.size} selected
                    </button>
                  )}
                </>
              )}
            </div>
          )}
          <ul className="space-y-1.5">
            {localDocs.map((d) => {
              const Icon = _iconFor(d.mime);
              const isSelected = selectedIds.has(d.file_id);
              return (
                <li
                  key={d.file_id}
                  className={`flex items-center gap-3 px-2.5 py-2 rounded-md group ${isSelected ? 'bg-sky-50 ring-1 ring-sky-300' : 'hover:bg-neutral-50'}`}
                  data-testid={`${testidPrefix}-doc-${d.file_id}`}
                >
                  {selectMode && trashEnabled && (
                    <button
                      type="button"
                      onClick={() => toggleSelect(d.file_id)}
                      className={`inline-flex items-center justify-center w-6 h-6 rounded-md shrink-0 ${isSelected ? 'bg-sky-600 text-white' : 'bg-white text-sky-600 border border-sky-300'}`}
                      data-testid={`${testidPrefix}-doc-checkbox-${d.file_id}`}
                      aria-label={isSelected ? 'Deselect document' : 'Select document'}
                    >
                      {isSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                    </button>
                  )}
                  <Icon className={`w-4 h-4 flex-shrink-0 ${_kind(d.mime) === 'image' ? 'text-blue-600' : 'text-neutral-500'}`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold text-[#0F172A] truncate">{d.name}</div>
                    <div className="text-[10px] text-neutral-500 uppercase tracking-wider">
                      {d.mime || 'file'} · {_fmtSize(d.size)}
                    </div>
                  </div>
                  {_kind(d.mime) !== 'other' && (
                    <button
                      type="button"
                      onClick={() => openPreview(d)}
                      className="text-[11px] font-semibold text-[#0078D4] hover:underline flex items-center gap-1"
                      data-testid={`${testidPrefix}-preview-${d.file_id}`}
                    >
                      <Eye className="w-3.5 h-3.5" /> Preview
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => download(d)}
                    className="text-[11px] font-semibold text-neutral-600 hover:text-[#0078D4] flex items-center gap-1"
                    data-testid={`${testidPrefix}-download-${d.file_id}`}
                  >
                    <Download className="w-3.5 h-3.5" /> Download
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {preview && (
        <div
          className="fixed inset-0 z-[110] bg-black/80 flex items-center justify-center p-4"
          onClick={closePreview}
          data-testid={`${testidPrefix}-preview-modal`}
        >
          <div
            className="bg-white rounded-xl overflow-hidden shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200">
              <div className="text-sm font-semibold text-[#0F172A] truncate">{preview.name}</div>
              <button
                type="button"
                onClick={closePreview}
                className="p-1.5 rounded-full hover:bg-neutral-100"
                data-testid={`${testidPrefix}-preview-close`}
                aria-label="Close preview"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-auto bg-neutral-950 flex items-center justify-center">
              {_kind(preview.mime) === 'image' ? (
                <img
                  src={preview.url}
                  alt={preview.name}
                  className="max-w-full max-h-[80vh] object-contain"
                />
              ) : _kind(preview.mime) === 'pdf' ? (
                <iframe
                  src={preview.url}
                  title={preview.name}
                  className="w-full h-[80vh] bg-white"
                />
              ) : (
                <div className="text-white text-sm p-8">Preview not available for this file type.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceivedDocumentsPanel;
